/**
 * Role title input with suggestions from the full career corpus.
 *
 * A real combobox: type two letters, get up to eight canonical role titles,
 * arrow keys to move, Enter to accept, Escape to dismiss. Free text is always
 * allowed — the list helps, it never blocks.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { loadRoleNames, searchRoles, type RoleSuggestion } from "@/lib/roleSuggest";

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  "aria-label"?: string;
}

export default function RoleTitleInput({
  value,
  onChange,
  placeholder = "Dream role (start typing…)",
  autoFocus,
  "aria-label": ariaLabel = "Dream role",
}: Props) {
  const listId = useId();
  const [roles, setRoles] = useState<RoleSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const blurTimer = useRef<number | null>(null);

  useEffect(() => {
    let alive = true;
    void loadRoleNames().then((r) => {
      if (alive) setRoles(r);
    });
    return () => {
      alive = false;
      if (blurTimer.current) window.clearTimeout(blurTimer.current);
    };
  }, []);

  const matches = useMemo(() => searchRoles(roles, value), [roles, value]);
  const showList = open && matches.length > 0;

  const accept = (name: string) => {
    onChange(name);
    setOpen(false);
    setActive(0);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={showList ? `${listId}-${active}` : undefined}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = window.setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (!showList) {
            if (e.key === "ArrowDown") setOpen(true);
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => (i + 1) % matches.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => (i - 1 + matches.length) % matches.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            accept(matches[active].name);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Matching roles"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-border bg-popover p-1 shadow-lg"
        >
          {matches.map((m, i) => (
            <li key={m.name} id={`${listId}-${i}`} role="option" aria-selected={i === active}>
              <button
                type="button"
                tabIndex={-1}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => accept(m.name)}
                className={`flex min-h-11 w-full flex-col items-start gap-0.5 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  i === active ? "bg-accent text-accent-foreground" : "text-foreground"
                }`}
              >
                <span className="font-medium">{m.name}</span>
                {(m.family || m.level) && (
                  <span className="text-[11px] text-muted-foreground">
                    {[m.family, m.level].filter(Boolean).join(" · ")}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
